import { Button, ButtonGroup, ColSplit, elHasSectionMargin, elMt8, Grid, InputGroup } from '@reapit/elements'
import React from 'react'
import { useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAttachment, usePlatformUpdate } from '@reapitform/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { IAgentBusinessDetailsResponse, IAgentSettingsData } from '../../../../../api/agent-settings'
import { FileState, InputFile } from '@reapitform/common-ui'
import { formatABN, formatACN, strictInput, yup } from '@reapitform/utils'

export interface IBusinessDetailsEditFormProps {
  onSuccess?: () => void
  onClose?: () => void
  onFailure?: () => void
  defaultData?: IAgentSettingsData['details']
  queryKey?: string
}

export const businessDetailsValidationSchema = yup.object().shape({
  businessName: yup
    .string()
    .required('Enter the business name')
    .maxLength(200, undefined, "Business name can't exceed 200 characters"),
  tradingName: yup
    .string()
    .required('Enter the trading name')
    .maxLength(200, undefined, "Trading name can't exceed 200 characters"),
  abn: yup.string().optional().default('').abn(),
  acn: yup.string().optional().default('').acn(),
  logo: yup
    .object({
      fileId: yup.string().required('Document still uploading'),
      fileName: yup.string().required('Document still uploading'),
      fileSize: yup.number().required('Document still uploading'),
      status: yup.string(),
    })
    .optional()
    .default(undefined),
})

export type TBusinessDetailsSchema = InferType<typeof businessDetailsValidationSchema>

const BusinessDetailsEditForm: React.FC<IBusinessDetailsEditFormProps> = ({
  onClose,
  onSuccess,
  defaultData,
  queryKey,
}) => {
  const queryClient = useQueryClient()
  const { getFileInformation, verifyFiles, isLoading } = useAttachment()
  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<TBusinessDetailsSchema>({
    defaultValues: {
      ...defaultData,
      acn: defaultData?.acn?.replaceAll(' ', ''),
      abn: defaultData?.abn?.replaceAll(' ', ''),
      logo: defaultData?.logo
        ? {
            fileId: defaultData.logo.fileId,
            fileName: defaultData.logo.fileName,
            fileSize: defaultData.logo.fileSize,
          }
        : undefined,
    },
    resolver: yupResolver(businessDetailsValidationSchema),
  })

  const [submitRequest, isSubmitLoading] = usePlatformUpdate<
    IAgentBusinessDetailsResponse,
    IAgentBusinessDetailsResponse
  >({
    path: '/api/Organization/BusinessDetails',
    method: 'PUT',
    headers: {},
    successMessage: 'Your business details information successfully updated!',
    errorMessage: 'Oopps... Failed to update your business details information!',
  })

  const handleFormSubmission = async (data: TBusinessDetailsSchema) => {
    const payload: IAgentBusinessDetailsResponse = {
      abn: formatABN(data.abn),
      acn: formatACN(data.acn),
      businessName: data.businessName,
      tradingName: data.tradingName,
      logoFileID: data.logo?.fileId,
      logoFileSize: data.logo?.fileSize,
    }

    if (data.logo?.fileId) await verifyFiles([data.logo.fileId])

    const result = await submitRequest(payload)

    if (!result) return
    let logoDetails

    if (result.logoFileID) {
      try {
        logoDetails = await getFileInformation(result.logoFileID)
      } catch (error) {
        console.warn("Attachment: Can't get logo information")
      }
    }

    if (queryKey) {
      queryClient.setQueryData<IAgentSettingsData['details']>([queryKey], { ...result, logo: logoDetails })
      queryClient.invalidateQueries({ queryKey: [queryClient] })
    }
    onSuccess?.()
  }

  const handleLogoChange = (fileData?: FileState) => {
    if (!fileData) return setValue('logo', undefined)
    setValue('logo', { fileId: fileData?.fileId ?? '', fileName: fileData?.fileName, fileSize: fileData.fileSize })
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmission)}>
      <Grid className={elHasSectionMargin}>
        <ColSplit>
          <InputGroup
            label="Business Name *"
            type="text"
            {...register('businessName')}
            errorMessage={errors.businessName?.message}
            maxLength={200}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Trading Name *"
            type="text"
            {...register('tradingName')}
            errorMessage={errors.tradingName?.message}
            maxLength={200}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="ABN"
            type="text"
            {...register('abn')}
            {...strictInput(/^[0-9]*$/)}
            errorMessage={errors.abn?.message}
            maxLength={11}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="ACN"
            type="text"
            {...register('acn')}
            {...strictInput(/^[0-9]*$/)}
            errorMessage={errors.acn?.message}
            maxLength={9}
          />
        </ColSplit>
        <ColSplit>
          <InputFile
            maximumFileSize="4 mb"
            acceptedFileType="image/logo"
            label="Logo"
            defaultData={getValues('logo')}
            onChange={handleLogoChange}
          />
        </ColSplit>
      </Grid>
      <ButtonGroup alignment="right" className={elMt8}>
        <Button intent="neutral" onClick={onClose} disabled={isSubmitLoading || isLoading}>
          Cancel
        </Button>
        <Button intent="primary" loading={isSubmitLoading || isLoading} disabled={isSubmitLoading || isLoading}>
          Save
        </Button>
      </ButtonGroup>
    </form>
  )
}

export default BusinessDetailsEditForm
